// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

// TTL is authoritative: server wall time may differ from the browser clock.
export function localCredentialTiming(credentials = {}, nowMs = Date.now()) {
  const { ttl_seconds, expires_at } = credentials || {};
  const lifetimeMs = ttl_seconds !== undefined
    ? (typeof ttl_seconds === 'number' ? ttl_seconds * 1000 : NaN)
    : (typeof expires_at === 'number' ? expires_at * 1000 - nowMs : NaN);
  if (!Number.isFinite(lifetimeMs) || lifetimeMs <= 0) return null;
  return { localExpiryMs: nowMs + lifetimeMs, lifetimeMs };
}

export function shouldReuseCredentials(credentials, timing, nowMs = Date.now()) {
  return Boolean(credentials?.username && credentials?.password &&
    Number.isFinite(timing?.localExpiryMs) && Number.isFinite(timing?.lifetimeMs) &&
    timing.lifetimeMs > 0 && timing.localExpiryMs - nowMs > timing.lifetimeMs * 0.2);
}

export function refreshDelayMs(credentials = {}, nowMs = Date.now()) {
  const timing = credentials?.localExpiryMs !== undefined
    ? credentials : localCredentialTiming(credentials, nowMs);
  if (!timing || !Number.isFinite(timing.localExpiryMs) ||
      !Number.isFinite(timing.lifetimeMs) || timing.lifetimeMs <= 0) return null;
  return Math.max(0, Math.floor(timing.localExpiryMs - nowMs - timing.lifetimeMs * 0.2));
}

export function isCredentialAuthFailure(error) {
  const cause = error?.originalError || error;
  return [4, 5, 134, 135].includes(cause?.code) ||
    /not authorized|not authorised|bad user(?:name| name) or password/i.test(cause?.message || '');
}

// Retry-After accepts either seconds or an HTTP date. Server delays are a floor.
export function credentialRetryDelayMs(attempt = 0, retryAfter, nowMs = Date.now()) {
  const backoff = Math.min(60000, 2000 * (2 ** Math.min(5, Math.max(0, attempt))));
  let serverDelay = 0;
  if (retryAfter !== undefined && retryAfter !== null && String(retryAfter).trim() !== '') {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
      serverDelay = seconds * 1000;
    } else {
      const date = Date.parse(retryAfter);
      if (Number.isFinite(date)) serverDelay = Math.max(0, date - nowMs);
    }
  }
  // Browser timers overflow above a signed 32-bit millisecond delay.
  return Math.min(2147483647, Math.max(backoff, serverDelay));
}
